import React, { useEffect, useMemo, useState, useCallback, Key, Ref } from 'react';
import { Cascader, Input } from 'antd';
import {
  Button,
  CustomFilter,
  EllipsisSpan,
  Form,
  Modal,
  Paging,
  Table,
  usePaging,
  useUpdate,
  useAsync,
} from '@maxtropy/components';
import {
  DeviceTypeTree,
  formatOptionData,
  getChildNodesByParentIds,
} from '@/shared/components/CascadingMultipleSelector/utils';
import { DefaultOptionType } from 'antd/es/cascader';
import { getEnvDeviceTree } from '@/api/envDevice';
import { getDeviceList, Device } from '@/api/device';
import { isNil } from 'lodash-es';

interface EnvDeviceChooseProps {
  value?: { deviceId: number; deviceName: string }[];
  onChange?: (value: { deviceId: number; deviceName: string }[]) => void;
  existingDeviceIds?: number[]; // 已存在的设备ID，用于置灰
  disabled?: boolean; // 禁用选择按钮
}

const columns = [
  {
    title: '设备编号',
    dataIndex: 'code',
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v} />,
  },
  {
    title: '设备名称',
    dataIndex: 'name',
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v} />,
  },
  {
    title: '所属类目',
    dataIndex: 'typeName',
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v} />,
  },
];

type SearchParams = {
  codeOrName?: string;
  typeId?: number[]; // 传给接口的是扁平的id数组
};

const EnvDeviceChoose = React.forwardRef((props: EnvDeviceChooseProps, ref: Ref<{ checkedValues: any[] }>) => {
  const { onChange, value, existingDeviceIds = [], disabled = false } = props;
  const [visible, setVisible] = useState<boolean>(false);
  const [checkValues, setCheckValues] = useState<any[]>([]);

  const [form] = Form.useForm();
  const pagingInfo = usePaging();
  const { pageOffset, pageSize, setTotalCount, setPageOffset } = pagingInfo;
  const [searchParams, setSearchParams] = useState<SearchParams>();
  const [updateState] = useUpdate();
  const deviceTypeData = useAsync(getEnvDeviceTree);

  useEffect(() => {
    if (isNil(value)) return;
    setCheckValues(value.map(k => ({ id: k.deviceId, name: k.deviceName })));
  }, [value]);

  const fetchDeviceList = useCallback(async () => {
    if (!deviceTypeData?.tree) return [];
    const typeId =
      searchParams?.typeId ??
      getChildNodesByParentIds(
        deviceTypeData?.tree.map(i => [i.id]),
        deviceTypeData?.tree
      );
    const res = await getDeviceList({
      codeOrName: searchParams?.codeOrName,
      typeId: typeId,
      page: pageOffset,
      size: pageSize,
    });
    if (res) setTotalCount(res?.total ?? 0);
    return res.list ?? [];
  }, [pageOffset, pageSize, searchParams, setTotalCount, updateState, deviceTypeData]);

  const [data, setData] = useState<Device[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (visible && deviceTypeData?.tree) {
      setLoading(true);
      fetchDeviceList()
        .then(list => setData(list))
        .finally(() => setLoading(false));
    }
  }, [visible, fetchDeviceList, deviceTypeData]);

  const onOk = () => {
    if (checkValues && checkValues.length !== 0) {
      onChange?.(
        checkValues.map(item => ({
          deviceId: item.id,
          deviceName: item.name,
        }))
      );
      setVisible(false);
    }
  };

  const onCancel = () => {
    setVisible(false);
  };

  const rowSelection = {
    type: 'checkbox' as any,
    onChange: (selectedRowKeys: Key[], selectedRows: any[]) => {
      setCheckValues(selectedRows);
    },
    preserveSelectedRowKeys: true,
    selectedRowKeys: checkValues.map(item => item.id),
    getCheckboxProps: (record: any) => ({
      disabled: existingDeviceIds.includes(record.id), // 已存在的设备置灰
    }),
  };

  const onFinish = (val: { codeOrName?: string; typeId?: number[][] }) => {
    const typeId = getChildNodesByParentIds(val.typeId, deviceTypeData?.tree);
    const params: SearchParams = {
      codeOrName: val.codeOrName,
      typeId: typeId,
    };
    setSearchParams(params);
    setPageOffset(1);
  };

  const onReset = () => {
    setSearchParams({});
    form.setFieldsValue({
      codeOrName: undefined,
      typeId: undefined,
    });
    setPageOffset(1);
  };

  const filter = (inputValue: string, path: DefaultOptionType[]) =>
    path.some(option => (option.label as string).toLowerCase().indexOf(inputValue.toLowerCase()) > -1);

  const filters = (
    <CustomFilter form={form} onFinish={onFinish} colSpan={12} onReset={onReset}>
      <Form.Item name="codeOrName" label="编号/名称">
        <Input placeholder="请输入编号或名称查询" />
      </Form.Item>
      <Form.Item name="typeId" label="所属类目">
        <Cascader
          options={formatOptionData(deviceTypeData)}
          allowClear={false}
          fieldNames={{ children: 'child' }}
          multiple
          maxTagCount="responsive"
          showSearch={{ filter }}
          placeholder="请选择所属类目"
        />
      </Form.Item>
    </CustomFilter>
  );

  return (
    <>
      <Button
        type="primary"
        disabled={disabled}
        onClick={() => {
          setVisible(true);
        }}
      >
        选择设备
      </Button>

      <Modal open={visible} title="选择设备" onCancel={onCancel} onOk={onOk} size="big">
        {filters}
        <Table
          loading={loading}
          rowSelection={rowSelection}
          sticky
          scroll={{ y: 300 }}
          rowKey="id"
          columns={columns}
          dataSource={data}
          selectedCount={checkValues?.length ?? 0}
          showSelectedCount
        >
          <Paging pagingInfo={pagingInfo} />
        </Table>
      </Modal>
    </>
  );
});

export default EnvDeviceChoose;
